import { EntitySubType, HAZARD_MARKERS, HazardType, MapboxLabelLayer, MapboxLayer } from '@/features';
import { i18n } from '@/locales';

const { t } = i18n.global;

export enum ToolbarMode {
  None = 'None',
  Filters = 'Filters',
  Overlays = 'Overlays',
}

export enum OverlayType {
  DistrictBoundaries = 'District boundaries',
  Weather = 'Weather',
}

export enum OverlaySubType {
  // District Boundaries
  DFESRegions = 'DFES regions',
  LocalGovernments = 'Local Governments',
  Pastoral = 'Pastoral',
  // Weather
  CycloneTrack = 'Cyclone track',
  RainRadar = 'Rain radar',
  ThunderstormTracker = 'Thunderstorm tracker',
  Satellite = 'Satellite',
  WindDirection = 'Wind direction and speed',
  FloodCatchments = 'Flood catchments',
}

// TODO: extract
export interface Dropdown {
  placeholder: string;
  sections: DropdownGroup[];
}
export interface DropdownGroup {
  label: string;
  items: DropdownItem[];

  hazardType?: HazardType;

  count?: number;
}
export interface DropdownItem {
  label: string;
  image?: string;
  overlay?: MapboxLayer;
  labelLayer?: MapboxLabelLayer;

  hazardType?: HazardType;
  hazardSubType?: (typeof EntitySubType)[keyof typeof EntitySubType][];

  count?: number;
}

export type DropdownSelected = Record<string, Record<string, boolean>>;

export const typeSearchPlaceholder = t('NKGbeys8C4dbBRHkTHE3l');

export const overlaySections: DropdownGroup[] = [
  {
    label: OverlayType.DistrictBoundaries,
    items: [
      { label: OverlaySubType.DFESRegions, overlay: MapboxLayer.dfesRegionsLayer, labelLayer: MapboxLabelLayer.dfesRegionsLayer },
      { label: OverlaySubType.LocalGovernments, overlay: MapboxLayer.lgaLayer, labelLayer: MapboxLabelLayer.lgaLayer },
      { label: OverlaySubType.Pastoral, overlay: MapboxLayer.pastoralLayer, labelLayer: MapboxLabelLayer.pastoralLayer },
    ],
  },
  {
    label: OverlayType.Weather,
    items: [
      { label: OverlaySubType.RainRadar, overlay: MapboxLayer.rainRadarLayer },
      { label: OverlaySubType.ThunderstormTracker, overlay: MapboxLayer.thunderstormTrackerLayer },
      { label: OverlaySubType.Satellite, overlay: MapboxLayer.weatherSatelliteLayer },
      { label: OverlaySubType.WindDirection, overlay: MapboxLayer.windDirectionLayer },
      { label: OverlaySubType.FloodCatchments, overlay: MapboxLayer.floodCatchmentLayer },
    ],
  },
];

export const layerSections = (): DropdownGroup[] => [
  {
    label: t('EDYpgcWMPmjxd6dXB1XWM'),
    items: [
      // Damaging Winds
      { label: t('dl0sCf9OcvKlslgcb3DmE'), image: HAZARD_MARKERS['ew-damaging-winds--emergency-warning'] },
      { label: t('e8sAGXp-1_bII3V9qPE3M'), image: HAZARD_MARKERS['ew-damaging-winds--watch-and-act'] },
      { label: t('opozjowRUuAXZesOr1O_N'), image: HAZARD_MARKERS['ew-damaging-winds--advice'] },

      // Flood
      { label: t('TUBdi7uw5EfrrE-joGAPN'), image: HAZARD_MARKERS['ew-flood--emergency-warning'] },
      { label: t('AVExRQkCRHvJCbrHdw-2u'), image: HAZARD_MARKERS['ew-flood--watch-and-act'] },
      { label: t('aeqcLBdAPkMADz72_SEA-'), image: HAZARD_MARKERS['ew-flood--advice'] },

      // Hazardous Materials
      { label: t('vgRtWCN9kxY1NQn2qsZah'), image: HAZARD_MARKERS['ew-hazmat-general-warning'] },

      // Heatwave
      { label: t('C7HMGIKAEZJ3EU6_2Nh3c'), image: HAZARD_MARKERS['ew-heatwave--emergency-warning'] },
      { label: t('6m4NUBo3vE4NDcu3xYmkf'), image: HAZARD_MARKERS['ew-heatwave--watch-and-act'] },
      { label: t('n_Fc8Zzqm_xkrt5O6sxQK'), image: HAZARD_MARKERS['ew-heatwave--advice'] },

      // Human Pandemic Warning
      { label: t('_eCnnH9IK21WP1qEYxCnA'), image: HAZARD_MARKERS['ew-state-wide-warning'] },

      // Smoke or Other Alert
      { label: t('kkqMSgk_8v7lQ8-HSvcaz'), image: HAZARD_MARKERS['ew-other-warning'] },

      // Storm
      { label: t('c9B7DCSs9bDX-aeGdTV8s'), image: HAZARD_MARKERS['ew-storm--emergency-warning'] },
      { label: t('BZfpyIc1qEsxGAmzXvKdS'), image: HAZARD_MARKERS['ew-storm--watch-and-act'] },
      { label: t('IK6VbGlK60AkArf6jPmPA'), image: HAZARD_MARKERS['ew-storm--advice'] },

      // Structure Fire
      { label: t('4M6F61FhO_MnwTUq633xm'), image: HAZARD_MARKERS['ew-structure-fire-warning'] },

      // Wildfire
      { label: t('RxaL17SqXZyV9BAOpNptR'), image: HAZARD_MARKERS['ew-bushfire--emergency-warning'] },
      { label: t('fBwJNbZpRX1wreEHZIxt-'), image: HAZARD_MARKERS['ew-bushfire--watch-and-act'] },
      { label: t('XrcnmbTXkiOSVBmVMafeG'), image: HAZARD_MARKERS['ew-bushfire--advice'] },
    ],
  },
  {
    label: t('x9pt5ALdrxsYA3DsOLgm6'),
    items: [
      { label: t('KT_4BlHJ7ZrJifsjj7JeT'), image: HAZARD_MARKERS['ew-damaging-winds'] },
      { label: t('6K91OiiEK1K8FyMMiiXO1'), image: HAZARD_MARKERS['ew-other-fire'] },
      { label: t('mzQr-TDOGQZBiS0JkvXI0'), image: HAZARD_MARKERS['ew-flood'] },
      { label: t('fB1MbPSnjVc_XvNwBuJOT'), image: HAZARD_MARKERS['ew-hazardous-materials'] },
      { label: t('ry9wjOHWz61gShaxRgrU4'), image: HAZARD_MARKERS['ew-human-pandemic'] },
      { label: t('a8ZrH1ps-FyZxCdbyxbzk'), image: HAZARD_MARKERS['ew-rescue'] },
      { label: t('Qw8n1vKJXyTz9pL4rWc2B'), image: HAZARD_MARKERS['ew-other-road-work'] },
      { label: t('TDAz1QS4kd1D0pn2-NQTn'), image: HAZARD_MARKERS['ew-storm'] },
      { label: t('e8f1j-PJEOtxjA3YglYOn'), image: HAZARD_MARKERS['ew-storm-damage'] },
      { label: t('nXT1Of0lxhlO9C2JvSJuw'), image: HAZARD_MARKERS['ew-other-incident'] },
    ],
  },
  {
    label: t('IdaFPyJzD-KUpjlTXGsao'),
    items: [
      { label: t('4pxFgiGCG3yT9K1woJ_Ll'), image: HAZARD_MARKERS['ew-evacuation-supports'] },
      { label: t('zZ2Tg7mII5zI4-lYyceQg'), image: HAZARD_MARKERS['ew-reception-centre'] },
    ],
  },
  {
    label: t('gDfl0cOHihK9_UA8AcJJT'),
    items: [
      { label: t('4viyfb2ojOTQlBKPwLDeX'), image: HAZARD_MARKERS['ew-facility-or-park-closure'] },
      { label: t('uiKiGZPB1d4_rSIZ4yB4h'), image: HAZARD_MARKERS['ew-power-outage'] },
      { label: t('qL9JfyMYjR3sSxVSVhO5l'), image: HAZARD_MARKERS['ew-road-closure'] },
      { label: t('zOSJy1oRtXyCmEyulLrUu'), image: HAZARD_MARKERS['ew-school-closure'] },
    ],
  },
];
